import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { toBill } from 'services/financial';
import { muiDataGridLocaleText } from 'services/i18n/mui';
import whiteHeaderTheme from 'theme/mui-data-grid-white-header-theme';

import { ACTIONS_BUTTON } from '../../../../../finance/NewInvoice/InvoiceTable/TableConstants';
import { Amount } from '../../../../../finance/shared/amount-view/Amount/Amount';
import { DefaultBudgetCell } from '../../../../../finance/shared/FinanseTableMUI/DefaultBudgetCell';
import { FinanceTableMUI } from '../../../../../finance/shared/FinanseTableMUI/FinanceTableMUI';
import { eventTypeLabel, FIELD_EVENT_TYPES } from '../../budgetConstant';
import { DIRECT_COST, PASS_THRU } from '../../modal/ExpenseType';

import { BudgetEventActionsButton } from './BudgetEventActionsButton/BudgetEventActionsButton';

import './BudgetEventsTable.scss';

export const BudgetEventsTable = ({ filteredEvents }) => {
  const stateFromLocalStorage = JSON.parse(localStorage?.getItem('BUDGET_EVENTS_TABLE_CONFIGURATION'));
  const [pinnedColumns, setPinnedColumns] = useState(
    stateFromLocalStorage?.pinnedColumns || {
      right: [ACTIONS_BUTTON]
    }
  );

  const columns = useMemo(
    () => [
      {
        headerName: 'Epoch',
        field: 'epochName',
        minWidth: 115,
        flex: 1
      },
      {
        headerName: 'Encounter',
        field: 'encounterName',
        minWidth: 135,
        flex: 1
      },
      {
        headerName: 'Item Group',
        field: 'itemGroupName',
        minWidth: 145,
        flex: 1
      },
      {
        headerName: 'Budget Event Type',
        field: 'type',
        flex: 1,
        minWidth: 190,
        renderCell: DefaultBudgetCell,
        valueGetter: (value, row) => eventTypeLabel[row.type]
      },
      {
        headerName: 'Event Name',
        field: 'fullEventName',
        minWidth: 145,
        flex: 1
      },
      {
        headerName: 'Excluded from Revenue',
        field: 'excludedFromRevenueProjection',
        minWidth: 210,
        flex: 1,
        valueGetter: (value, row) => {
          if (FIELD_EVENT_TYPES.includes(row.type)) {
            return resolveExcludedFromRevenueFlag(value);
          } else {
            return resolveExcludedFromRevenueFlag(row?.optionalOnProtocolSetup);
          }
        }
      },
      {
        accessor: 'clientAmount',
        Header: 'Client',
        headerClassName: 'amount-header',
        headerName: 'Client',
        field: 'clientAmount',
        type: 'number',
        flex: 1,
        minWidth: 110,
        valueGetter: (value, row) =>
          row.type === 'PATIENT_REIMBURSEMENT'
            ? toBill(0)
            : row.type === 'EXPENSE_VARIABLE'
            ? toBill(row.maxAllowanceAmount)
            : toBill(row.clientAmount),
        renderCell: ({ row }) => {
          if (row.type === 'PATIENT_REIMBURSEMENT') {
            return (
              <ZeroAmountWithTooltip tooltipTitle="This study is eligible for submitting patient reimbursements. Amounts will vary." />
            );
          }
          if (row.type === 'EXPENSE_VARIABLE') {
            if (row.maxAllowanceAmount === 0) {
              return <ZeroAmountWithTooltip tooltipTitle="There is no maximum for this expense." />;
            } else {
              return <Amount coinsAmount={row.maxAllowanceAmount} showDollarSign />;
            }
          } else {
            return <Amount coinsAmount={row.clientAmount} defaultZeroValue={''} showDollarSign />;
          }
        }
      },
      {
        field: 'siteAmount',
        headerName: 'Site',
        flex: 1,
        minWidth: 95,
        type: 'number',
        valueGetter: (value, row) =>
          row.type === 'EXPENSE_VARIABLE' && !row.managementFee
            ? toBill(row.maxAllowanceAmount)
            : toBill(row.siteAmount),
        renderCell: ({ row }) =>
          row.type === 'EXPENSE_VARIABLE' ? (
            row.maxAllowanceAmount === 0 ? (
              <ZeroAmountWithTooltip tooltipTitle="There is no maximum for this expense." />
            ) : (
              <Amount coinsAmount={row.managementFee ? row.siteAmount : row.maxAllowanceAmount} showDollarSign />
            )
          ) : (
            <Amount coinsAmount={row.siteAmount} defaultZeroValue={''} showDollarSign />
          )
      },
      {
        accessor: 'patientAmount',
        Header: 'Patient',
        headerClassName: 'amount-header',
        field: 'patientAmount',
        headerName: 'Patient',
        minWidth: 115,
        flex: 1,
        type: 'number',
        valueGetter: (value, row) => (row.type === 'PATIENT_REIMBURSEMENT' ? toBill(0) : toBill(row.patientAmount)),
        renderCell: ({ row }) => {
          if (row.type === 'PATIENT_REIMBURSEMENT') {
            return (
              <ZeroAmountWithTooltip tooltipTitle="Patients are eligible to receive the full approved reimbursement amount." />
            );
          } else {
            return <Amount coinsAmount={row.patientAmount} defaultZeroValue={''} showDollarSign />;
          }
        }
      },
      {
        field: 'vendorAmount',
        headerName: 'Vendor',
        minWidth: 115,
        flex: 1,
        type: 'number',
        valueGetter: (value, row) => toBill(row.vendorAmount),
        renderCell: ({ row }) => <Amount coinsAmount={row.vendorAmount} defaultZeroValue={''} showDollarSign />
      },
      {
        accessor: 'elligoAmount',
        Header: 'Net',
        headerClassName: 'amount-header',
        field: 'elligoAmount',
        headerName: 'Net',
        minWidth: 95,
        flex: 1,
        type: 'number',
        valueGetter: (value, row) => (row.type === 'PATIENT_REIMBURSEMENT' ? toBill(0) : toBill(row.elligoAmount)),
        renderCell: ({ row }) =>
          row.type === 'PATIENT_REIMBURSEMENT' ? (
            <div className="budget-event-table column-value">$0.00</div>
          ) : (
            <Amount coinsAmount={row.elligoAmount} showDollarSign />
          )
      },
      {
        field: 'overhead',
        headerName: 'OH',
        width: 95,
        valueGetter: value => (value ? 'Yes' : 'No'),
        renderCell: ({ row }) => {
          return row.overhead ? 'Yes' : 'No';
        }
      },
      {
        field: 'withholding',
        headerName: 'WH',
        width: 95,
        valueGetter: value => (value ? 'Yes' : 'No'),
        renderCell: ({ row }) => {
          return row.withholding ? 'Yes' : 'No';
        }
      },
      {
        field: 'managementFee',
        headerName: 'Fee',
        width: 95,
        valueGetter: value => (value ? 'Yes' : 'No'),
        renderCell: ({ row }) => {
          return row.managementFee ? 'Yes' : 'No';
        }
      },
      {
        field: 'siteAmountType',
        headerName: 'Site PT/DC',
        width: 95,
        valueGetter: value => {
          switch (value) {
            case PASS_THRU:
              return 'PT';
            case DIRECT_COST:
              return 'DC';
            default:
              return 'N/A';
          }
        }
      },
      {
        field: 'triggerName',
        headerName: 'Protocol Event',
        flex: 1,
        valueGetter: (value, row) => (['EXPENSE_FIXED', 'EXPENSE_VARIABLE'].includes(row.type) ? '' : row.triggerName),
        renderCell: DefaultBudgetCell
      },
      {
        field: 'nonInvoiceable',
        headerName: 'Non-Invoiceable',
        width: 180,
        valueGetter: value => (value ? 'Yes' : 'No')
      },
      {
        headerName: 'Actions',
        field: 'actions',
        type: 'actions',
        width: 70,
        resizable: false,
        renderCell: ({ row }) => <BudgetEventActionsButton row={row} />
      }
    ],
    []
  );

  const handlePinnedColumnsChange = useCallback(updatedPinnedColumns => {
    setPinnedColumns({
      ...updatedPinnedColumns,
      right: [...updatedPinnedColumns.right.filter(column => column !== ACTIONS_BUTTON), ACTIONS_BUTTON]
    });
  }, []);

  const getTogglableColumns = useCallback(
    columns => columns.filter(column => ![ACTIONS_BUTTON].includes(column.field)).map(column => column.field),
    []
  );

  return (
    <ThemeProvider theme={whiteHeaderTheme}>
      <FinanceTableMUI
        sx={{
          padding: '0 20px 0 20px'
        }}
        localStorageTableName="BUDGET_EVENTS"
        data-testid="budget-details-table"
        getRowId={row => row?.id}
        rows={filteredEvents}
        columns={columns}
        getTogglableColumns={getTogglableColumns}
        footerElementsCount={filteredEvents.length}
        initialState={{
          columns: {
            columnVisibilityModel: { triggerName: false, nonInvoiceable: false, siteAmountType: false }
          },
          pinnedColumns
        }}
        localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        disableRowSelectionOnClick
        tableVersion="1.02"
      />
    </ThemeProvider>
  );
};

const ZeroAmountWithTooltip = ({ tooltipTitle }) => {
  const CustomizedTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} title={tooltipTitle} placement="right" arrow />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 'none',
      fontSize: '90%'
    }
  });
  return (
    <CustomizedTooltip>
      <div className="budget-event-table column-value">
        <span>
          $0.00
          <span className="text-colour">*</span>
        </span>
      </div>
    </CustomizedTooltip>
  );
};

function resolveExcludedFromRevenueFlag(value) {
  return value ? 'Yes' : '—';
}
